// types/location-features.ts

import { DEFAULT_LOCALE, LOCALES, type Locale } from '@/lib/i18n/config'
import { parseAlcoholSaleHours, type AlcoholSaleHours } from '@/lib/retail/age/alcoholHours'

export type ReceiptChannel = 'print' | 'sms' | 'email' | 'none'
export type DefaultReceiptChannel = ReceiptChannel | 'ask'
export type KitchenMode = "kds" | "printer" | "kds_display"
/** Control de inventario: qué hacer si una venta dejaría un ingrediente sin stock. */
export type InventoryControlMode = 'off' | 'warn' | 'block'
export type DisplayLayout = 'compact' | 'cards' | 'hero_upsell' | 'split_upsell'
/**
 * Interruptor de sugerencias de la Pantalla del cliente (PR 5a): 'none' = apagado;
 * 'engine' = el motor de lib/suggestions elige. Los tres valores viejos
 * ('featured' | 'best_sellers' | 'complementary') eran fuentes propias del display
 * que ya no existen: se LEEN como "encendido" y se normalizan a 'engine' solo
 * cuando el dueño vuelve a tocar el toggle. Nunca se reescriben en masa.
 */
export type DisplayUpsellSource = 'none' | 'engine' | 'featured' | 'best_sellers' | 'complementary'
export type DisplayUpsellStyle = 'grid' | 'hero'

// E2 — canales de origen + métodos de pago externos
export type KdsTimerStart = 'created' | 'paid' | 'in_progress'
export type KdsBarMetric = 'count' | 'oldest' | 'avg' | 'over_target'

export type ExternalPaymentMethod = 'zelle' | 'cashapp' | 'venmo' | 'other_external'
export type PriceBasis = 'cash' | 'card'

/**
 * Alcance del Lote de etiquetas (retail, spec 2026-10-20 §5.5): 'shared' = todos ven todo lo
 * que se juntó en el local; 'per_user' = cada persona ve solo lo suyo. Es un filtro de LECTURA
 * sobre una sola tabla (retail_label_batch): cambiarlo no borra ni mueve filas.
 */
export type LabelBatchScope = 'shared' | 'per_user'

/**
 * Cómo elige la comida el cliente en las superficies públicas (página web y
 * kiosko). 'classic' = todas las opciones en una pantalla, con scroll.
 * 'guided' = una pregunta por pantalla para lo obligatorio, y los extras al
 * final. Lo elige el dueño desde su panel; NO es una bandera de despliegue.
 */
export type SalesFlow = 'classic' | 'guided'

/**
 * Config por método de pago EXTERNO (no procesado). El sistema sólo REGISTRA que
 * la orden se pagó por este método — no lo procesa. `price_basis` decide qué
 * total del dual pricing se cobra: 'cash' (net/take-home, el default — estos
 * métodos no llevan fee de tarjeta) o 'card' (con el gross). Decisión 2026-06-05:
 * por método, default cash.
 */
export interface ExternalPaymentMethodConfig {
  method: ExternalPaymentMethod
  enabled: boolean
  price_basis: PriceBasis
}

/**
 * Premio de la variante CHECKOUT del juego del kiosko (jugar antes de pagar,
 * 2026-08-22). El dueño define la lista en su admin (página kiosk-game); el
 * cliente elige por cuál juega. `percent` = % de descuento sobre el subtotal
 * (100 = "tu comida gratis"); `tolerance_cs` = ventana de win en CENTÉSIMAS
 * alrededor de 10.00 (0 = exacto, más ancho = más fácil — el dueño gradúa
 * premio grande/difícil vs premio chico/fácil). Cada sesión congela un
 * snapshot: editar esta lista no cambia partidas en vuelo.
 */
export interface KioskGameCheckoutPrizeConfig {
  id: string
  label: string
  percent: number
  tolerance_cs: number
}

import type { EightySixPermission } from "@/lib/pos/eightySixPermission"

export interface SupervisorSettings {
  refund: boolean
  void: boolean
  manual_discount: boolean
  comp_item: boolean

  /**
   * Crear un ítem desde el POS, o cambiarle el PRECIO a uno que ya existe,
   * exige PIN de encargado.
   *
   * Default `true`, al revés que `void` — y a propósito. El criterio de "no
   * cambiarle el comportamiento a quien nunca lo configuró" protege hábitos que
   * YA existen; acá no hay ninguno que proteger: fijar precios desde la caja es
   * una función nueva y nadie la ha usado sin PIN. Y lo que hace no es una
   * operación de caja reversible: deja un número que se le cobra a todos los
   * clientes siguientes, en el menú web y el kiosko incluidos, hasta que
   * alguien lo note. Está del lado de `refund` y `manual_discount`, que también
   * nacen en `true`.
   *
   * Solo muerde cuando la operación TOCA DINERO: crear siempre lo hace (fija un
   * precio), pero editar solo cuando el número cambia. Corregirle una letra al
   * nombre de un plato no despierta al encargado.
   *
   * Consecuencia que hay que conocer: un local SIN ningún encargado/dueño con
   * PIN no podrá crear ni recotizar desde el POS hasta que configure uno o
   * apague este ajuste. Es el mismo muro que ese local ya tiene en refunds y
   * descuentos (`no_supervisors` es fallo duro en todo el repo); acá no se le
   * abre una excepción, porque una excepción por "no hay supervisores" sería
   * también la forma de saltarse el control.
   */
  menu_item_pricing: boolean
  discount_threshold_pct: number | null
  discount_threshold_amount: number | null

  /**
   * Aprobación REMOTA: cuando una acción ya requiere supervisor (los toggles de
   * arriba), el POS puede además ofrecer "pedírselo al encargado por teléfono" en
   * vez de exigir el PIN en esa misma tablet.
   *
   * CONVIVE con el PIN local, no lo reemplaza: el PIN sigue siendo el camino
   * rápido cuando el encargado está ahí, y la pantalla de espera SIEMPRE deja
   * volver a él (el remoto nunca puede dejar la caja sin salida).
   *
   * Default `false` a propósito — mismo criterio que `void` y que
   * `admin_pin_on_shared_terminal`: un local que nunca lo configuró no cambia de
   * comportamiento, y encenderlo depende de que el dueño tenga la app con push.
   */
  remote_approval: boolean

  /** Cuánto vive una solicitud sin respuesta antes de expirar sola y liberar la
   *  caja con un mensaje claro. Acotado por REMOTE_APPROVAL_TTL_BOUNDS. */
  remote_approval_ttl_seconds: number

  /** A quién se le notifica. 'supervisors' = todo el nivel supervisión+ del local
   *  (managers y dueños); 'owners' = sólo dueños, para locales donde el manager de
   *  turno es justamente quien no debe autorizarse a sí mismo. */
  remote_approval_notify: 'supervisors' | 'owners'
}

/** Límites de `remote_approval_ttl_seconds`. Piso 60 s: menos que eso no le da
 *  tiempo a nadie a sacar el teléfono. Techo 30 min: una caja no puede quedarse
 *  esperando una tarde entera. */
export const REMOTE_APPROVAL_TTL_BOUNDS = { min: 60, max: 1800, default: 300 } as const

export interface LocationFeatures {
  // Sprint 1
  tips_enabled: boolean
  /** Aviso en la caja cuando un cliente deja propina en la pantalla del cliente o en
   *  el quiosco (spec 2026-09-13-avisos-caja). Lo decide el dueño en Configuración
   *  del POS › Propinas. Default OFF. */
  tips_alert_pos: boolean
  /** Marca de propina en el KDS, para agradecer al entregar (spec 2026-09-13-avisos-caja):
   *  "$ PROPINA" en la tarjeta de cocina y el panel de listas (PR 4) y "Dejó propina" en
   *  Despacho (PR 3). Sin monto. La clave dice expo por historia: vale para todo el KDS.
   *  Lo decide el dueño en Configuración del POS › Propinas. Default OFF. */
  tips_mark_expo: boolean
  sms_on_receipt: boolean
  module_employees: boolean
  module_customers: boolean
  module_reports: boolean
  module_kds: boolean
  module_online_ordering: boolean
  pos_accent_color: string | null
  pos_logo_url: string | null

  // Sprint 2
  tip_percentages: number[]
  default_receipt_channel: DefaultReceiptChannel
  enabled_receipt_channels: ReceiptChannel[]
  cash_enabled: boolean
  card_enabled: boolean
  customer_capture_required: boolean
  /** Cuándo se pide el teléfono/nombre del cliente en el POS.
   *  'before' (default) = teléfono + nombre antes de cobrar.
   *  'after' = teléfono + nombre después del pago, antes del recibo (spec 2026-06-02).
   *  'name_first' = nombre antes (→ comanda de cocina), teléfono después
   *  (→ CRM/recibo). Spec 2026-06-05.
   *  'name_only' = SOLO nombre, antes de cobrar; el teléfono no se pide nunca.
   *  Existe porque hasta 2026-08-08 la única forma de dejar de pedir teléfono era
   *  apagar `customer_capture_required`, que se llevaba también el nombre — y el
   *  nombre es lo que la cocina necesita para cantar el pedido. OJO: sin teléfono
   *  no hay SMS de "tu pedido está listo" ni fila en CRM; y el paso del RECIBO
   *  sigue pidiendo número aparte si 'sms' está en `enabled_receipt_channels`. */
  customer_capture_timing: 'before' | 'after' | 'name_first' | 'name_only'
  pos_theme: 'light' | 'dark'

  // Sprint 2 — Supervisor
  supervisor_settings: SupervisorSettings
  /**
   * ¿Quién puede agotar (86) platos e ingredientes desde el POS?
   * Ver `lib/pos/eightySixPermission.ts` — el default reproduce el
   * comportamiento previo a que existiera el ajuste.
   */
  pos_86_permission: EightySixPermission

  /**
   * Entrar al panel de administración DESDE una caja registradora exige PIN de
   * manager/owner, aunque el navegador traiga la sesión de la dueña.
   *
   * El porqué (feedback Zulianisimo 2026-07-26 — "el cajero no está autorizado a
   * ver reportes de venta"): el gateo por rol YA existe y funciona, pero solo
   * cuando el empleado entra con SU propia cuenta. La tablet del local corre con
   * la sesión de la dueña, así que `access.role` es merchant_owner y la rama de
   * empleado ni se evalúa — quien tenga la tablet ve los 19 reportes.
   *
   * Default `false` a propósito. La marca de terminal (lib/server/terminalDevice)
   * se estampa en TODA tablet con POS de la flota, así que encenderlo por defecto
   * le cobraría un PIN —en cada visita a su propio dashboard— a un dueño solo que
   * usa su iPad como caja: fricción que no pidió. Es una política sobre el propio
   * personal, así que la enciende quien tiene personal. Mismo criterio con el que
   * nació `supervisor_settings.void`.
   *
   * NO afecta el portal del empleado (/my/*): ver tu propia nómina o fichar no
   * necesita PIN de supervisor.
   */
  admin_pin_on_shared_terminal: boolean

  // Inventory
  inventory_enabled: boolean

  /**
   * Control de inventario — qué hacer cuando una venta dejaría un ingrediente
   * sin stock. Sólo aplica si `inventory_enabled`. Default 'off' = comportamiento
   * histórico (nada cambia para los locales existentes).
   *
   *  'off'   → se vende; el descuento simplemente se salta (queda en logs).
   *  'warn'  → se vende SIEMPRE, pero se registra la sobreventa para el dueño.
   *  'block' → se rechaza ANTES de cobrar (422). Nunca después de un cargo: el
   *            descuento de tarjeta ocurre en el webhook con la tarjeta ya
   *            aprobada, así que bloquear ahí dejaría dinero cobrado sin orden.
   */
  inventory_control: InventoryControlMode

  /**
   * «Pocas»: con cuántas unidades o menos un plato se marca en ámbar en el chip de
   * la caja y lleva «¡Quedan pocas!» en el kiosko y el menú en línea. Default 5.
   * Un local que produce de a 200 querrá más; uno de postres del día, menos.
   * Leerlo SIEMPRE con resolveLowStockUnits (el jsonb se edita a mano).
   */
  low_stock_units: number

  /**
   * «Sumar producción» desde el chip de la caja: ¿descuenta también los crudos
   * de la receta de producción (carne, masa…)? Default true = como siempre.
   * false = «producción simple»: sube solo el terminado, valorado a su costo
   * actual. Para el local que cuenta lo terminado y no los crudos — descontar
   * crudos que nadie contó solo fabrica negativos. El admin (Ingredientes →
   * Producción) sigue descontando crudos: ahí se produce a propósito.
   */
  pos_production_deducts_raw: boolean

  // Retail
  retail_catalog: boolean

  /**
   * Etiquetas de estante (retail): pantalla de etiquetas, campo de contenido en el editor y la
   * importación, y sección de impresora. Requiere también `retail_catalog`. Capacidad opt-in:
   * `retail_catalog` ya está prendido en restaurantes reales, así que no alcanza como puerta.
   */
  shelf_labels: boolean

  /**
   * Alcance del Lote de etiquetas (ver LabelBatchScope). Lo cambia el dueño. Solo se lee con
   * `shelf_labels && retail_catalog`. Default 'shared'.
   */
  label_batch_scope: LabelBatchScope

  /**
   * Alta rápida de productos retail (spec 2026-10-20 §7): prende, solo en el local que lo pide,
   * las bases abiertas, la IA de enriquecimiento, las pestañas del escaneo, la ficha 2c, el
   * escáner USB global y la pregunta de impuesto por defecto. Requiere también `retail_catalog`.
   * Capacidad opt-in: apagada por defecto, y sin ella el escaneo queda como en `main`.
   */
  retail_quick_entry: boolean

  /**
   * Impuesto por defecto de los productos que se dan de alta escaneando (spec §4.5): `true` =
   * nacen gravables, `false` = nacen exentos, `null` = todavía no se le preguntó al dueño.
   * Es tri-estado a propósito: `null` NO es `false` (un local sin respuesta conserva el default
   * de siempre de createRetailProduct). Solo se lee con `retail_catalog && retail_quick_entry`.
   */
  retail_default_taxable: boolean | null

  /** Campaña Venezuela — captura de donaciones opcionales del cliente al pagar.
   *  Opt-in por local; default OFF. La tira pública es independiente de este flag. */
  donation_enabled: boolean

  /**
   * Bar management — barra como capacidad para restaurantes que también operan
   * un bar (carta de tragos, inventario de botellas, pour cost, KDS de barra).
   * Reutiliza el motor de inventario; las bebidas son items con is_bar=true.
   * Default OFF; se activa por local.
   */
  bar_management: boolean

  /**
   * Tienda online — gate del catálogo especial. El POS separa la comida del bar
   * (is_bar) y de la tienda física (is_retail), pero el microsite público
   * (/order/[slug]) cargaba TODO sin filtro: tragos y productos físicos salían
   * online sin que el dueño lo decidiera. Con estos flags salen solo opt-in
   * (Ajustes → Funcionalidades). Default OFF: al 2026-07-27 ningún local real
   * tenía ítems is_bar/is_retail activos (solo redy-test), así que ocultarlos
   * no le quita menú a nadie.
   */
  online_show_bar: boolean
  online_show_retail: boolean
  /**
   * PR 1/2 del spec súper online. Apagada = la tienda online oculta y rechaza
   * lo retail que todavía no sabe cobrar (peso, variantes, retail con
   * modificadores). Solo tiene efecto con `online_show_retail` prendida.
   */
  online_retail_v2: boolean

  /**
   * Verificación de edad al vender alcohol/tabaco (caja de súper, PR 4). Apagada =
   * ni pantalla de edad ni horario: el cobro es byte a byte el de siempre. Solo se
   * lee como `=== true`.
   */
  age_verification: boolean

  /**
   * Horario de venta de alcohol del local (7 ventanas, domingo primero; `until`
   * puede pasar de 24:00 hasta 30:00). `null` = horario de Texas off-premise
   * (`TEXAS_OFF_PREMISE_HOURS`). Solo tiene efecto con `age_verification`. Un jsonb
   * inválido se lee como `null`.
   */
  alcohol_sale_hours: AlcoholSaleHours | null

  /**
   * Paquetería interestatal (Fase 1b, EasyPost Forge). Mitad DEL DUEÑO del
   * gate doble: la otra mitad es `shippingGateOpen()` (server-side, kill
   * switch de plataforma — ver lib/shipping/gate.ts). Con cualquiera de los
   * dos apagado, cero diferencia observable (I28). Default OFF: nace como
   * capacidad opt-in, igual que retail_catalog y bar_management.
   *
   * `/api/shipping/quote` y loadMicrositeData todavía leen esta clave como
   * JSONB crudo (`rawFeatures.shipping`) porque nacieron ANTES de que este
   * campo se tipara acá (T8/T9 vs T12) — mismo patrón que GATE-COUPONS. No
   * hace falta migrarlos: `locations.features` es la misma columna JSONB en
   * los dos caminos, así que el valor coincide.
   */
  shipping: boolean

  /**
   * Kiosko — contraparte del gate online para el autoservicio, con flags
   * separados por canal a propósito: retail en el kiosko es self-checkout
   * legítimo (hay staff en el local), mientras que tragos en autoservicio sin
   * verificación de edad no deberían prenderse solo porque el dueño los mostró
   * en su página web. Default OFF — mismo censo que online_show_*: ocultarlos
   * no le quita menú a ningún local real.
   */
  kiosk_show_bar: boolean
  kiosk_show_retail: boolean

  /** Flujo de venta de la página pública y el kiosko. Ver SalesFlow. */
  sales_flow: SalesFlow

  /**
   * Tiempo de espera real según la cocina en la tienda online (PR K, #2589).
   * `false` = tiempo fijo (`pickup_wait_minutes`); `'confirmation'` = real solo
   * después de pagar; `'everywhere'` (o `true`, valor viejo) = real en toda la
   * tienda. Lo lee `liveWaitMode` (lib/microsite/waitEstimate.ts). Se elige en
   * Página web → «Tiempo de espera» (LiveWaitPanel).
   */
  live_wait_time: boolean | 'confirmation' | 'everywhere'

  /**
   * Juego "Parala en 10.00" del kiosko (spec 2026-08-09).
   * - `kiosk_game_available`: superadmin ofrece la promo al local (rollout del
   *   piloto). Sin esto, el merchant ni ve la card.
   * - `kiosk_game_enabled`: CACHE operativo que lee el kiosko. La verdad legal
   *   es la fila viva de kiosk_game_acceptances; este flag se prende al aceptar
   *   y se apaga al revocar. Jamás editarlo a mano sin tocar la aceptación.
   * - `kiosk_game_camera_enabled`: gate de la cámara de reacciones, POR LOCAL.
   *   Se enciende al aceptar los términos (decisión de producto 2026-08-14) y
   *   se apaga al revocar; superadmin puede apagarlo por local si el hardware
   *   sufre (el spike A77 de #1509 queda como verificación recomendada, ya no
   *   como gate). El default en DEFAULT_FEATURES sigue false — locales sin
   *   aceptación no cambian.
   */
  kiosk_game_available: boolean
  kiosk_game_enabled: boolean
  kiosk_game_camera_enabled: boolean

  /**
   * Variante CHECKOUT del juego (2026-08-22): se juega JUSTO ANTES de pagar y
   * el premio es un descuento (cupón percent de un solo uso) que el cliente
   * elige de `kiosk_game_checkout_prizes`. Requiere la misma aceptación de
   * términos que el juego original (`kiosk_game_enabled` + fila viva de
   * kiosk_game_acceptances) Y `coupons_enabled` (el premio viaja por el rail
   * de cupones). Mientras está en true, la invitación POST-pago no se ofrece:
   * un solo juego por orden, en un solo momento.
   */
  kiosk_game_checkout_enabled: boolean
  kiosk_game_checkout_prizes: KioskGameCheckoutPrizeConfig[]

  // Kitchen mode
  pos_kitchen_mode: KitchenMode

  // Order type
  order_type_enabled: boolean
  order_type_required: boolean
  default_order_type: 'dine_in' | 'takeout' | 'delivery' | 'curbside' | 'drive_thru' | 'ship' | null
  /** Q1 / D1 — operator-enabled order types. Defaults to legacy ['takeout','dine_in'] when missing. */
  enabled_order_types?: ('takeout' | 'dine_in' | 'delivery' | 'curbside' | 'drive_thru' | 'ship')[]
  table_number_enabled: boolean
  /**
   * Cuando true (default), elegir "Aquí" (dine_in) abre el mapa de mesas
   * (TablePicker) para asignar una mesa. Cuando false, dine_in solo registra
   * el tipo de orden sin pedir mesa — para restaurantes que usan "Aquí / Para
   * llevar" únicamente como info de empaque y no manejan mesas. No afecta el
   * modo servidor (pos_layout="server"), que siempre muestra el FloorMapView.
   */
  table_map_enabled: boolean

  /**
   * Q1 / H7 — Nightlife floor map unification opt-in.
   * Cuando true, las consumers (bar/page.tsx) leen de floor_sections en vez
   * de floorplan_zones. Default false → comportamiento idéntico al de hoy.
   * Se activa cuando el admin corre migrateNightlifeFloorplan().
   */
  floor_map_unified?: boolean

  /**
   * Salón v2 (spec 2026-10-10): FloorStage + mesas "Tinta con sillas" en
   * editor, selector del POS, salón en vivo, hostess y Host/Salón. Apagada
   * por defecto; se enciende por local. El camino viejo se retira en PR 1b.
   */
  floor_v2?: boolean

  /**
   * Nightlife: cuando true, los ítems de barra nacen 'pending' y fluyen por la
   * cola del barback (/bar/runner) hasta entregarse. Default false = los ítems
   * nacen 'delivered' (comportamiento histórico, cuentan al tab al instante).
   */
  bar_queue_enabled?: boolean

  // Modules — GO LIVE launcher visibility
  module_kiosk: boolean
  module_display: boolean
  module_events: boolean

  // Modules — nav/launcher visibility (simplificación menú 2026-07-10).
  // SOLO visibilidad de navegación (sidebar/launcher/config hub); no cambian
  // comportamiento del POS (el mapa de mesas del POS sigue en table_map_enabled).
  module_tables: boolean       // "Mesas" del sidebar + Piso en vivo + Host/Salón del launcher
  module_menu_board: boolean   // Pizarra de menú (launcher + tarjeta de config)
  module_loyalty: boolean
  module_reviews: boolean
  module_growth: boolean

  /**
   * Motor de delivery propio (Uber Direct): despachar un courier contra una
   * orden existente y seguirla hasta entregarse. Controla la visibilidad del
   * módulo en nav/launcher y el botón de despacho en el POS.
   * Default OFF; se activa por local. La config fina (proveedor, radio,
   * % del fee trasladado) vive en la tabla location_delivery_config.
   */
  module_delivery?: boolean

  /**
   * Cypher Suite (GoHighLevel white-label): CRM, inbox y automatizaciones del
   * comercio. Sólo gobierna lo que el dueño VE. Por dónde salen los SMS lo
   * decide `location_cypher_credentials.sms_status`, que es otra compuerta a
   * propósito: apagar este flag por un impago no puede matar un recibo.
   */
  module_cypher?: boolean

  /** GATE-COUPONS — activa el sistema de cupones (crear/editar/canjear). La
   *  clave es exactamente `coupons_enabled`: PR-2/PR-3 ya la leen cruda desde
   *  `locations.features->>'coupons_enabled'` (`app/api/online-orders/route.ts`,
   *  `.../session/route.ts`) — renombrarla rompe esos rieles de dinero en
   *  silencio. Default OFF; se activa por local desde Ajustes → Funcionalidades. */
  coupons_enabled: boolean

  /** PROMOCIONES (Fase A) — ofertas por tiempo: precio de oferta por producto,
   *  variante o categoría, con horario y canales. El precio se fija al PEDIR.
   *  Default OFF: sin esta bandera las rutas de cobro no mandan las claves de
   *  promoción y `record_sale` / `append_tab_round` / `checkout_tab` insertan
   *  exactamente lo de hoy. Spec: docs/superpowers/specs/2026-10-20-promociones-design.md */
  promotions: boolean

  // Auto-print service
  auto_kitchen_print: boolean  // print kitchen ticket for every confirmed order
  auto_receipt_print: boolean  // print receipt automatically on confirmation
  auto_refund_receipt_print: boolean  // print refund comprobante automatically when a refund is processed

  // Customer display — Wave 2
  display_layout: DisplayLayout
  display_upsell_source: DisplayUpsellSource
  display_upsell_style: DisplayUpsellStyle
  /** El cliente ve armarse cualquier plato con modificadores en su pantalla, no
   *  solo la pizza. Apagada por defecto: es una función nueva y visible.
   *  NO habilita los extras tocables — esa es live_build_touch, del PR 2. */
  live_build_mirror: boolean
  /** El cliente puede TOCAR sus opciones y extras, no solo verlos. Depende de
   *  `live_build_mirror`: sin espejo no hay nada que tocar. Apagada por defecto —
   *  carga el riesgo de D5 (el cliente mueve el total sin aprobación previa). */
  live_build_touch: boolean
  /** Apaga las animaciones decorativas del display del cliente (válvula de
   *  rendimiento para tablets modestas). Default false. También se respeta
   *  prefers-reduced-motion a nivel CSS. */
  display_reduce_motion: boolean

  // Whitelabel / branding — paid-tier feature.
  // When false (free tier default), Redy branding badge is visible on customer-facing surfaces
  // (display, kiosk, microsite). When true (paid plans), the badge is hidden.
  // This flag is NOT exposed in the merchant admin UI — only Redy ops can toggle it.
  hide_powered_by_branding: boolean

  // Temporary: manual card payment confirmation when North webhooks are not
  // being delivered (production whitelist pending). Superadmin-only toggle.
  manual_card_confirm: boolean

  /** GATE-TABLET-ROLE — habilita la gestión de rol de tablet en el dispositivo:
   *  banner de rol no coincidente, fijar rol desde el lanzador, y el gate de
   *  cobro con tarjeta sin caja/lector. Default OFF; rollout por local desde
   *  superadmin. NO se expone en el admin del merchant. */
  tablet_role_management: boolean

  /** GATE-CARD-RAIL-UNIFY — D135 como rail principal de tarjeta (A77 de
   *  fallback) y habilita el money path split-PAX. Default OFF; se activa
   *  por local desde superadmin. */
  unified_card_rail: boolean

  /** GATE-DURABLE-SESSION — cuando true, el startup gate usa la ruta
   *  resiliente: un tablet configurado NUNCA se limpia/desloguea por un
   *  corte transitorio de Supabase o una query offline RLS-denied.
   *  Default OFF; se activa por local desde superadmin. */
  durable_session: boolean

  /** POS lee con identidad de dispositivo (read-token JWT) en vez de la sesión
   *  del dueño. Default OFF; piloto por-location. Ver spec 2026-07-05. */
  pos_identity: boolean

  /** GATE-TAP-TO-PAY — habilita Apple Tap to Pay (cobro contactless en el
   *  iPhone vía el North/Payanywhere SDK). Default OFF (oscuro): con esto en
   *  false el dispositivo NO se enrola para Tap to Pay. Se activa por local
   *  tras el grant de Apple + North cert. Ver docs/2026-06-18-TTP_FACEID_DESIGN.md. */
  tap_to_pay_enabled: boolean

  /** GATE-APP-LOCK — bloqueo biométrico (Face ID) de la app en superficies de
   *  operador iOS. Default OFF; se activa por local. Cuando ON, al reanudar tras
   *  `app_lock_timeout_seconds` o en cold-start, exige Face ID (fallback = PIN de
   *  supervisor con lockout por-dispositivo). Ver docs/2026-06-18-TTP_FACEID_DESIGN.md §6. */
  app_lock_enabled: boolean
  /** Segundos en background tras los que el app-lock vuelve a exigir auth. */
  app_lock_timeout_seconds: number

  // E2 — canales de origen flexibles + pago.
  // enabled_channels = canales que el cajero puede ELEGIR al teclear una orden
  //   manual (E3); pos/kiosk/online siempre son válidos (los setean sus flujos).
  // external_payment_methods = métodos sólo-registro (Zelle/Venmo…), metadata.
  // allow_deferred_payment = opt-in para disparar a cocina y cobrar después
  //   (la "cuenta abierta" sin mesa).
  enabled_channels: string[]
  external_payment_methods: ExternalPaymentMethodConfig[]
  allow_deferred_payment: boolean

  // KDS — tiempos de servicio (Sprint 1)
  kds_service_target_minutes: number
  kds_warning_minutes: number
  kds_critical_minutes: number
  kds_timer_start: KdsTimerStart
  kds_bar_metrics: KdsBarMetric[]
  kds_bar_enabled: boolean
  /** Pantalla de Despacho (expo) en el KDS. Opt-IN, local por local. */
  kds_expo_enabled: boolean
  /** Paso "Iniciar"/"Tomar" antes de "Lista". OFF = un solo toque (default:
   *  en prod el 60–80 % de las órdenes se marcaban listas sin pasar por él). */
  kds_start_step_enabled: boolean
  /** «Aceptar orden» obligatorio en el KDS para pedidos online/QR (como
   *  DoorDash). OFF (default) = la tarjeta online se trata como cualquier otra. */
  kds_accept_online_enabled: boolean
  /** Con pantallas por estación: la vista general no marca lista una orden
   *  hasta que cada ítem de estación esté tachado (por la estación o a mano);
   *  si no, pide «Sacar igual». OFF (default) = la general marca lista libre. */
  kds_general_waits_stations: boolean
  /** Botón «Llamar al cliente» en las tarjetas del KDS: SMS de copy fijo
   *  pidiéndole que se acerque. OFF (default) = sin botón. */
  kds_call_customer_enabled: boolean
  /** Horas tras las que una orden "lista" que nadie marcó entregada pasa sola a
   *  entregada (cron kds-ready-sweep). 0 = nunca. */
  kds_ready_auto_clear_hours: number
  /**
   * Sustituciones en pedidos online («si algo no está disponible»): el cliente elige
   * en el checkout qué hacer si falta un ítem y el local propone reemplazos desde el
   * KDS/caja. OFF (default) = nada de esto se muestra ni se crea.
   * Spec: docs/superpowers/specs/2026-09-28-sustituciones-pedidos-online-design.md
   */
  online_substitutions_enabled: boolean
  /** Minutos que el cliente tiene para responder a una propuesta de reemplazo antes de que se aplique su preferencia. Restaurantes 5; retail 30-60. */
  substitution_timeout_minutes: number
  /**
   * Sugerencias en la tienda online (hoja "¿Le agregas algo más?"). ON por
   * defecto: la web siempre sugirió; desde el PR 5b lo hace el motor de
   * lib/suggestions en vez del azar. Apagarlo quita la fila de sugerencias de
   * la hoja (la confirmación "¡Agregado!" se queda).
   */
  online_suggestions_enabled: boolean
  /**
   * Hoja «¿Lo acompañas con algo?» en el kiosko al agregar un plato desde el
   * menú (PR 6 del motor de sugerencias). OFF por defecto: tapa el menú un
   * momento, así que cada local la enciende desde Sugerencias. Solo actúa si
   * kiosk_config.kiosk_upsell_enabled también está encendido.
   */
  kiosk_item_added_suggestions_enabled: boolean

  /**
   * PRINT-MASTER (#1164): exige que la superficie que reclama la comanda sea la
   * atada a LA impresora de cocina del local, no cualquiera que "tenga impresora".
   * opt-IN, default OFF (fail-open) — encenderlo cambia quién imprime, así que va
   * local por local con el registro de impresoras ya verificado.
   *
   * Declarado aquí en la re-auditoría 2026-07-20: el flag existía en el código
   * (lib/print/printMasterGuard) pero NO en este tipo ni en el panel superadmin, así
   * que la ÚNICA forma de encenderlo era editar el jsonb a mano en la DB. Resultado:
   * NULL en 11/11 locales ⇒ #1164 shipeó 100% inerte y el bug que arregla seguía vivo
   * en todos. Ahora es encendible desde superadmin (toggle), sin SQL crudo.
   * OJO antes de encender en `zulianisimo`: su impresora está registrada con host MAC
   * (001162213A99) en vez de IP — corregir el registro primero o el guard no matchea.
   */
  kitchen_print_master_enforce: boolean

  /**
   * Idioma en el que la cocina recibe el pedido: comanda impresa, KDS y recibo.
   *
   * Existe porque el catálogo ahora es bilingüe. Hasta que lo fue, la pregunta
   * no tenía sentido: sólo había un nombre por plato. Con traducciones, un
   * cliente que pide en inglés en el kiosko genera una línea cuyo texto podría
   * salir en cualquiera de los dos idiomas, y quien la lee no es el cliente
   * sino el cocinero.
   *
   * El nombre se resuelve UNA vez, al crear la orden, y se congela en
   * `order_items.item_name` / `order_item_modifiers.option_name`. Por eso este
   * flag no reescribe historia: cambiarlo afecta las órdenes nuevas y las
   * viejas siguen diciendo lo que decían el día que se cobraron.
   *
   * Default `es` = el canónico ⇒ ningún local cambia de comportamiento.
   */
  kitchen_language: Locale
}

/** Base value for any field not present in the DB. */
export const DEFAULT_FEATURES: LocationFeatures = {
  // Sprint 1
  tips_enabled: true,
  tips_alert_pos: false,
  tips_mark_expo: false,
  sms_on_receipt: false,
  module_employees: true,
  module_customers: true,
  module_reports: true,
  module_kds: true,
  module_online_ordering: true,
  pos_accent_color: null,
  pos_logo_url: null,

  // Sprint 2
  tip_percentages: [15, 18, 20, 25],
  default_receipt_channel: 'ask',
  enabled_receipt_channels: ['print', 'sms', 'email', 'none'],
  cash_enabled: true,
  card_enabled: true,
  customer_capture_required: true,
  customer_capture_timing: 'before',
  pos_theme: 'light',

  // Sprint 2 — Supervisor
  // Reproduce el comportamiento previo al ajuste: con PIN de POS solo
  // encargado/dueño agotan; sin PIN la tablet es la frontera de confianza.
  // Ningún local cambia al desplegar — solo gana poder endurecerlo.
  pos_86_permission: "supervision",

  supervisor_settings: {
    refund: true,
    // `void` ya NO apaga el PIN de lo que salió a cocina: eso lo exige siempre
    // voidRequiresSupervisor (spec 2026-10-07). Este toggle solo extiende el PIN a
    // órdenes que nunca salieron (pending: aborto de cobro, quiosco en efectivo).
    void: false,
    manual_discount: true,
    comp_item: true,
    menu_item_pricing: true,
    discount_threshold_pct: null,
    discount_threshold_amount: null,
    remote_approval: false,
    remote_approval_ttl_seconds: 300,
    remote_approval_notify: 'supervisors',
  },

  // Los locales que nunca lo configuraron NO cambian de comportamiento.
  admin_pin_on_shared_terminal: false,

  // Inventory
  inventory_enabled: true,
  // Default 'off': los locales existentes NO cambian de comportamiento. Encenderlo
  // es una decisión explícita del dueño (un conteo malo en 'block' traba la caja).
  inventory_control: "off",
  low_stock_units: 5,
  pos_production_deducts_raw: true,
  retail_catalog: false,
  age_verification: false,
  alcohol_sale_hours: null,
  shelf_labels: false,
  label_batch_scope: 'shared',
  retail_quick_entry: false,
  retail_default_taxable: null,
  donation_enabled: false,
  bar_management: false,
  // Tienda online: bar y retail ocultos salvo opt-in del dueño.
  online_show_bar: false,
  online_show_retail: false,
  online_retail_v2: false,
  // Paquetería (Fase 1b): opt-in, mitad del gate doble con GATE_SHIPPING.
  shipping: false,
  // Kiosko: ídem — cada canal decide por su cuenta.
  kiosk_show_bar: false,
  kiosk_show_retail: false,

  // Flujo de venta: por defecto clásico (todas las opciones en una pantalla).
  sales_flow: 'classic',
  live_wait_time: false,

  // Juego del kiosko: apagado en los tres niveles hasta piloto con gate legal.
  kiosk_game_available: false,
  kiosk_game_enabled: false,
  kiosk_game_camera_enabled: false,
  // Variante checkout: apagada y sin premios hasta que el dueño la configure.
  kiosk_game_checkout_enabled: false,
  kiosk_game_checkout_prizes: [],

  // Kitchen mode
  pos_kitchen_mode: "kds",

  // Order type
  order_type_enabled: false,
  order_type_required: false,
  default_order_type: null,
  enabled_order_types: ['takeout', 'dine_in'],
  table_number_enabled: false,
  // Default true → restaurantes existentes siguen viendo el mapa al elegir
  // "Aquí". Opt-out explícito para quienes no manejan mesas.
  table_map_enabled: true,

  // Q1 / H7 — Nightlife unification opt-in. Default OFF para no romper
  // clientes nightclub existentes que usan floorplan_zones.
  floor_map_unified: false,

  floor_v2: false,

  // Nightlife: cola del barback. Default OFF = ítems nacen 'delivered'.
  bar_queue_enabled: false,

  // Modules — GO LIVE launcher visibility
  module_kiosk: false,
  module_display: true,
  module_events: false,

  // Modules — nav/launcher visibility. Default ON: se apagan por local para
  // simplificar el menú de clientes básicos.
  module_tables: true,
  module_menu_board: true,
  module_loyalty: true,
  module_reviews: true,
  module_growth: true,

  // Motor de delivery propio (Uber Direct). Default OFF: se activa por local
  // una vez configurado location_delivery_config.
  module_delivery: false,

  // Cypher Suite (CRM/GHL white-label). Default OFF: sólo visibilidad para el
  // dueño; el SMS del local lo decide sms_status, no este flag.
  module_cypher: false,

  // GATE-COUPONS — default OFF; se activa por local desde Ajustes →
  // Funcionalidades. Clave exacta que PR-2/PR-3 ya leen cruda (ver comentario
  // en la interfaz arriba). Sin esto en true, ningún cupón se puede canjear.
  coupons_enabled: false,

  // PROMOCIONES (Fase A) — default OFF; ningún local cambia hasta encenderla.
  promotions: false,

  // Auto-print service
  auto_kitchen_print: false,
  auto_receipt_print: false,
  auto_refund_receipt_print: false,

  // Customer display — Wave 2
  display_layout: 'compact',
  display_upsell_source: 'none',
  display_upsell_style: 'grid',
  live_build_mirror: false,
  live_build_touch: false,
  display_reduce_motion: false,

  // Branding (free tier shows badge by default)
  hide_powered_by_branding: false,

  // O-01 AUDIT FIX: Default-enable manual card confirm as a safety net for
  // connectivity-challenged environments (food trucks, events). When North
  // webhooks don't arrive (network issues, whitelist pending), cashiers can
  // manually mark card payments as confirmed. Superadmins can disable per-location.
  manual_card_confirm: true,

  // GATE-TABLET-ROLE — default OFF; se activa por local desde superadmin.
  tablet_role_management: false,

  // GATE-CARD-RAIL-UNIFY — default ON (fleet-wide 2026-06-27): D135 = rail
  // principal de tarjeta (A77 de fallback) + habilita split-PAX. Blockers
  // resueltos: migración tender_id ya en prod (sin pérdida de BRIC) + fixes
  // #515/#520 (sin doble cobro, recuperación idempotente, CAS en flip a paid).
  // No-op para locales sin D135 emparejado (resolveActiveCardRail → "card"/A77).
  // Opt-out explícito para negocios que operan con A77 primario y/o cuyo MID EPX
  // del D135 aún no está habilitado: features.unified_card_rail = false
  // (al 2026-06-27: raiz58, pepitos-waao).
  unified_card_rail: true,

  // GATE-DURABLE-SESSION — default ON (fleet-wide 2026-06-25) after #657 (durable
  // session) + #669 (remote revocation) + #713 (logout fully revokes) + overnight
  // verification. A location can still opt OUT with features.durable_session = false.
  durable_session: true,

  // GATE-POS-IDENTITY — default ON (fleet-wide 2026-07-11) tras el piloto
  // (maktub/pepitos-waao/redy-test corriéndolo bien, incl. redy-test a través de
  // 9 bumps de epoch) + gate de realtime RLS verificado CERRADO por construcción
  // (el POS suscribe realtime con el claim kds_location_id, el mismo del KDS que
  // ya funciona en vivo; device_role solo gatea lecturas REST) + #1038 self-heal
  // desplegado (cura el limbo "config sin deviceToken"). Es la cura de fondo de
  // "logueado pero vacío" (el POS lee con identidad de DISPOSITIVO, no con la
  // sesión del dueño). Un local puede opt-OUT con features.pos_identity = false.
  // OJO: el flag SOLO tiene efecto en tablets NATIVAS — PosGateWrapper lo gatea a
  // Capacitor.isNativePlatform() para que el POS-en-navegador (sin deviceToken)
  // siga leyendo con la sesión de dueño en vez de caer a "Sin conexión".
  pos_identity: true,

  // GATE-TAP-TO-PAY — default OFF (oscuro). Se activa por local tras grant Apple + North cert.
  tap_to_pay_enabled: false,

  // GATE-APP-LOCK — default OFF. 120s de timeout cuando se active.
  app_lock_enabled: false,
  app_lock_timeout_seconds: 120,

  // E2 — opt-in; apagado/vacío por default (locales existentes intactos).
  enabled_channels: [],
  external_payment_methods: [],
  allow_deferred_payment: false,

  // KDS — tiempos de servicio (Sprint 1)
  kds_service_target_minutes: 12,
  kds_warning_minutes: 12,
  kds_critical_minutes: 18,
  kds_timer_start: 'paid',
  kds_bar_metrics: ['count', 'oldest', 'avg', 'over_target'],
  kds_bar_enabled: true,
  kds_expo_enabled: false,
  kds_start_step_enabled: false,
  kds_accept_online_enabled: false,
  kds_general_waits_stations: false,
  kds_call_customer_enabled: false,
  kds_ready_auto_clear_hours: 4,
  online_substitutions_enabled: false,
  substitution_timeout_minutes: 5,
  online_suggestions_enabled: true,
  kiosk_item_added_suggestions_enabled: false,

  // PRINT-MASTER sigue opt-IN (default OFF, fail-open): encenderlo cambia quién
  // imprime, así que se activa local por local desde el panel superadmin.
  kitchen_print_master_enforce: false,

  // Idioma de la cocina = el canónico. Los locales existentes no cambian:
  // la comanda sigue diciendo exactamente lo que decía.
  kitchen_language: DEFAULT_LOCALE,
}

/** Safe merge: DB fields + defaults for missing fields. */
export function resolveFeatures(raw: unknown): LocationFeatures {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FEATURES }
  const merged = { ...DEFAULT_FEATURES, ...(raw as Partial<LocationFeatures>) }
  merged.supervisor_settings = {
    ...DEFAULT_FEATURES.supervisor_settings,
    ...(merged.supervisor_settings ?? {}),
  }
  // Solo estas dos claves se normalizan (jsonb editable a mano); el resto, igual que siempre.
  const r = raw as { age_verification?: unknown; alcohol_sale_hours?: unknown }
  merged.age_verification = r.age_verification === true
  merged.alcohol_sale_hours = parseAlcoholSaleHours(r.alcohol_sale_hours)
  return merged
}

/** Límites del umbral de «pocas»: 1 como mínimo (0 sería «nunca avisar», que ya
 *  es apagar el auto-86) y 999 como techo contra un dedo de más. */
export const LOW_STOCK_UNITS_MIN = 1
export const LOW_STOCK_UNITS_MAX = 999

/**
 * El umbral de «pocas» del local, validado: el valor viene de un jsonb que se
 * puede editar a mano. Basura (texto, negativo, decimal, fuera de rango) ⇒ el
 * default, nunca un umbral que haga desaparecer o gritar la etiqueta en todo.
 */
export function resolveLowStockUnits(features: Pick<LocationFeatures, "low_stock_units">): number {
  const raw = features.low_stock_units
  return typeof raw === "number" && Number.isInteger(raw) && raw >= LOW_STOCK_UNITS_MIN && raw <= LOW_STOCK_UNITS_MAX
    ? raw
    : DEFAULT_FEATURES.low_stock_units
}

export function effectiveKitchenMode(features: LocationFeatures): KitchenMode {
  return features.module_kds === false ? "printer" : features.pos_kitchen_mode
}

/**
 * Idioma en el que se congela el nombre de cada línea al crear la orden.
 *
 * Valida contra `LOCALES` en vez de confiar en el tipo: el valor viene de un
 * jsonb que se puede editar a mano, y un idioma inválido dejaría la comanda en
 * blanco (`translations['fr']` no existe ⇒ el helper caería al canónico, pero
 * mejor decidirlo aquí y una sola vez). Cualquier basura ⇒ el canónico.
 */
export function resolveKitchenLocale(features: LocationFeatures): Locale {
  const raw = features.kitchen_language
  return typeof raw === 'string' && (LOCALES as readonly string[]).includes(raw)
    ? (raw as Locale)
    : DEFAULT_LOCALE
}

/**
 * ¿El NOMBRE del cliente se captura ANTES del pago? Cuando true, el nombre
 * entra en el INSERT de la orden y por lo tanto aparece en la comanda de cocina.
 * True en 'before', 'name_first' y 'name_only'. Desactivado si la captura no es
 * obligatoria o si es una enmienda (el cliente ya está en la orden).
 */
export function resolveCaptureNameBefore(
  features: LocationFeatures,
  isAmendment: boolean,
): boolean {
  if (!features.customer_capture_required || isAmendment) return false
  const t = features.customer_capture_timing
  return t === 'before' || t === 'name_first' || t === 'name_only'
}

/**
 * ¿El TELÉFONO del cliente se captura DESPUÉS del pago? Cuando true, el INSERT
 * deja customer_phone en null y el teléfono se asocia luego vía
 * PATCH /api/orders/[id]/customer. True en 'after' y 'name_first'. Desactivado
 * si la captura no es obligatoria o si es una enmienda (el cliente ya está en la orden).
 *
 * En 'name_only' es false Y no hay captura previa de teléfono: ese local no pide
 * número en ningún momento del cobro (a propósito).
 */
export function resolveCapturePhoneAfter(
  features: LocationFeatures,
  isAmendment: boolean,
): boolean {
  if (!features.customer_capture_required || isAmendment) return false
  const t = features.customer_capture_timing
  return t === 'after' || t === 'name_first'
}
